const regex = {
    mobileRegExp: /^(\+\d{1,3}[- ]?)?\d{10}$/,
    emailRegExp: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
    passwordRegExp: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\^$*.{}\]()?!\-@"#%&/\\,><':;|_~`+=])(?=.{8,20}$)/,
    contractIdRegExp: /^[a-zA-Z0-9-]{0,15}$/,
    paymentAmountRegExp: /^\d*\.?\d{0,2}$/,
};

export const defaultTimerCount = 30;

export const ET_TIMEZONE = 'America/New_York';
const ET_DATE_OPTIONS: Intl.DateTimeFormatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };

/**
 * Formats a date/timestamp in Eastern Time (America/New_York, EST or EDT as applicable),
 * regardless of the user's browser timezone. Backend timestamps are UTC.
 */
export const formatInET = (
    value: string | number | Date,
    options: Intl.DateTimeFormatOptions = ET_DATE_OPTIONS,
): string => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: ET_TIMEZONE }).format(date);
};

/** Short Eastern Time zone abbreviation ("EST" or "EDT") in effect at the given moment. */
export const getETZoneAbbr = (value: string | number | Date = new Date()): string => {
    const date = value instanceof Date ? value : new Date(value);
    const part = new Intl.DateTimeFormat('en-US', { timeZone: ET_TIMEZONE, timeZoneName: 'short' })
        .formatToParts(date)
        .find(p => p.type === 'timeZoneName');
    return part?.value ?? 'ET';
};
export const getTimeAgo = (utcTime: string | number | Date) => {
    if (utcTime === null || utcTime === undefined) return '--';

    const date = new Date(utcTime);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    const timeFormats = [
        { limit: 3, singular: 'Just Now', plural: 'Just Now', divisor: 1 },
        { limit: 60, singular: 'Sec Ago', plural: 'Secs Ago', divisor: 1 },
        { limit: 3600, singular: 'Min Ago', plural: 'Mins Ago', divisor: 60 },
        { limit: 86400, singular: 'Hour Ago', plural: 'Hours Ago', divisor: 3600 },
        { limit: 604800, singular: 'Day Ago', plural: 'Days Ago', divisor: 86400 },
        { limit: 2419200, singular: 'Week Ago', plural: 'Weeks Ago', divisor: 604800 },
        { limit: 29030400, singular: 'Month Ago', plural: 'Months Ago', divisor: 2419200 },
        { limit: Infinity, singular: 'Year Ago', plural: 'Years Ago', divisor: 29030400 },
    ];

    let i = 0;
    while (i < timeFormats.length) {
        const { limit, singular, plural, divisor } = timeFormats[i];
        if (diffInSeconds < limit) {
            if (i === 0) return singular;
            const value = Math.floor(diffInSeconds / divisor);
            return `${value} ${value > 1 ? plural : singular}`;
        }
        i += 1;
    }
    return '--';
};

const formatToUSDate = (isoString: string | number) => {
    if (!isoString) return '';

    // If input is a number (e.g., year from total row), return it as is
    if (typeof isoString === 'number') {
        return isoString.toString();
    }

    // Check if the ISO string has zeros after T (e.g., T00:00:00.000Z, T00:00:00Z, or T00:00:00+00:00)
    const zeroTimePattern = /^(\d{4}-\d{2}-\d{2})T00:00:00(\.000)?(Z|(\+|-)\d{2}:\d{2})?$/i;
    const match = isoString.match(zeroTimePattern);

    // If timestamp is all zeros, return the date part directly
    if (match) {
        const datePart = match[1]; // e.g., '2029-06-06'
        // Convert YYYY-MM-DD to MM/DD/YYYY format
        const [year, month, day] = datePart.split('-');
        return `${month}/${day}/${year}`;
    }

    // Otherwise, format normally (in Eastern Time)
    return formatInET(isoString, ET_DATE_OPTIONS);
};

const formatToUSDateWithTime = (isoString: string) => {
    return `${formatInET(isoString, ET_DATE_OPTIONS)}, ${formatInET(isoString, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    })} ET`;
};

const formatDateWithTimeWithSeconds = (isoString: string) => {
    return `${formatInET(isoString, ET_DATE_OPTIONS)}, ${formatInET(isoString, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    })}`;
};
/**
 * Masks the email address to prevent it from being visible in DOM inspection
 * @param email - The email address to mask
 * @returns Masked email string (e.g., "us******@example.com")
 */
const getMaskedEmailForProp = (email: string): string => {
    if (!email || !email.includes('@')) return email || '';

    const [name, domain] = email.split('@');
    if (name.length <= 2) return email;

    const firstTwo = name.slice(0, 2);
    const masked = '*'.repeat(Math.max(4, name.length - 2));

    return `${firstTwo}${masked}@${domain}`;
};

const getMaskedPhoneNumberForProp = (phoneNumber: string): string => {
    if (!phoneNumber) return phoneNumber || '';
    return `******${phoneNumber.slice(-4)}`;
};
/**
 * Detects if the input value is an email or phone number
 * @param value - The input value to check
 * @returns 'email' | 'phone' | null
 */
const detectInputType = (value: string): 'email' | 'phone' | null => {
    if (!value || !value.trim()) return null;

    const trimmedValue = value.trim();

    // Check if it's an email (contains @ and matches email regex)
    if (trimmedValue.includes('@') && regex.emailRegExp.test(trimmedValue)) {
        return 'email';
    }

    // Check if it's a phone number (matches phone regex)
    // Remove common formatting characters for validation
    const phoneDigits = trimmedValue.replace(/[\s\-()]/g, '');
    if (regex.mobileRegExp.test(phoneDigits)) {
        return 'phone';
    }

    return null;
};

/**
 * Formats currency amount using International numbering system
 * - Commas placed every 3 digits from the right (e.g., 12,345,678)
 * - Uses en-US locale for International standard formatting
 * - NOT using Indian numbering system (Lakhs, Crores, or 1,23,45,678 format)
 * @param amount - The amount to format (string or number)
 * @returns Formatted currency string with $ prefix (e.g., "$12,345.67")
 */
const formatCurrency = (amount: string | number): string => {
    if (amount === null || amount === undefined || amount === '') return '$0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '$0.00';
    // Using 'en-US' locale ensures International numbering system (commas every 3 digits)
    const formatted = Math.abs(numAmount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return `$${formatted}`;
};

const formatTime = (time: string) => {
    const dateObj = new Date(time);
    let hours = dateObj.getHours();
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours === 0 ? 12 : hours;
    return `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
};

const isValidContractId = (value: string) => {
    // Regex for Alphanumeric, Minimum 2 to Maximum 15 characters
    return regex.contractIdRegExp.test(value);
};

const isValidSSNNumber = (value: string) => {
    // Validates US SSN format: XXX-XX-XXXX with 9 digits
    // First group (area): not 000 or 666
    // Second group (group): not 00
    // Third group (serial): not 0000
    if (!value || typeof value !== 'string') return false;
    // Remove hyphens for validation
    const digitsOnly = value.replace(/\D/g, '');
    // Check if exactly 9 digits
    if (digitsOnly.length !== 9) return false;
    // Extract groups
    const areaNumber = digitsOnly.substring(0, 3);
    const groupNumber = digitsOnly.substring(3, 5);
    const serialNumber = digitsOnly.substring(5, 9);
    // Check first group: not 000 or 666
    if (areaNumber === '000' || areaNumber === '666') {
        return false;
    }
    // Check second group: not 00
    if (groupNumber === '00') {
        return false;
    }
    // Check third group: not 0000
    if (serialNumber === '0000') {
        return false;
    }
    return true;
};

const isValidPostalCode = (value: string) => {
    // Regex for Postal Code
    // Alphanumeric, single space allowed (not multiple), no special characters, length 5-9
    // Pattern: alphanumeric + optional(space + alphanumeric) repeated
    const postalCodeRegExp = /^[a-zA-Z0-9]+(?:\s[a-zA-Z0-9]+)*$/;
    return postalCodeRegExp.test(value);
};

/**
 * getGreeting.ts
 * Utility function to get greeting based on time of day
 */

const getGreeting = (): string => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
};

const getMimeType = (filename: string): string => {
    const extension = filename.split('.').pop()?.toLowerCase() || '';
    const mimeTypes: { [key: string]: string } = {
        // Documents
        pdf: 'application/pdf',
        doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xls: 'application/vnd.ms-excel',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ppt: 'application/vnd.ms-powerpoint',
        pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        // Images
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        bmp: 'image/bmp',
        webp: 'image/webp',
        svg: 'image/svg+xml',
        // Text
        txt: 'text/plain',
        csv: 'text/csv',
        html: 'text/html',
        xml: 'application/xml',
        json: 'application/json',
        // Email
        eml: 'message/rfc822',
        // Other
        rtf: 'application/rtf',
        zip: 'application/zip',
        rar: 'application/x-rar-compressed',
    };
    return mimeTypes[extension] || 'application/octet-stream';
};

/**
 * Whether the UI should offer in-app preview for this filename.
 * Legacy `.doc` (Word 97–2003) is hidden by default; the viewer cannot render it reliably.
 * For other extensions, callers should hide preview after the preview API returns an error or empty payload.
 */
const isDocumentPreviewEligible = (filename: string): boolean => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    return ext !== 'doc';
};

/**
 * iOS Safari (and in-app webviews) largely ignore the `download` attribute on
 * `<a>` tags pointed at `blob:` URLs — the click just navigates/opens the blob
 * instead of saving it. Detect that case so callers can fall back to opening
 * the blob in a new tab, where the user can use the native share/save sheet.
 */
const isIOSDownloadUnreliable = (): boolean => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
    return isIOS;
};

/**
 * Triggers a browser download for a Blob, with a mobile-safe fallback:
 * on iOS, the `download` attribute is unreliable for blob URLs, so the blob
 * is opened in a new tab instead, letting the user save it from there.
 */
const downloadBlobSafely = (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob);
    if (isIOSDownloadUnreliable()) {
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const restrictedExtensions = [
    'exe',
    'bat',
    'cmd',
    'com',
    'pif',
    'scr',
    'vbs',
    'js',
    'jar',
    'msi',
    'dll',
    'sh',
    'ps1',
    'app',
    'deb',
    'rpm',
    'dmg',
    'pkg',
    'apk',
    'ipa',
    'bin',
    'run',
    'sh',
    'bash',
    'zsh',
];
// Allowed file extensions based on requirements
const ALLOWED_FILE_EXTENSIONS = ['jpg', 'jpeg', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv', 'xml', 'png'];

/** Minimal contract shape required by payment-eligibility helpers. */
interface PaymentContract {
    AspireId?: string | null;
    AspireRecordId?: string | null;
    AccountType?: string | null;
    IsLoan?: boolean | null;
    AutoPay?: boolean | null;
    OutstandingBalance?: number | string | null;
}

/** Minimal open-invoice shape required by payment-eligibility helpers. */
interface PaymentInvoice {
    aspireContractId?: string | null;
    AspireContractId?: string | null;
}

// Helper to check if Pay Now should be disabled based on contract type and autopay
// Logic: AccountType (B=Business/Commercial, I=Individual/Consumer), isLoan (true=loan, false=lease), AutoPay
// Disable Pay Now if:
// - Card payment + non-consumer-loan (B/I + !IsLoan, or B + IsLoan): Always disabled
// - Consumer Lease (I + !IsLoan): Always disabled
// - Commercial Lease (B + !IsLoan): Always disabled
// - Commercial Loan (B + IsLoan): Disabled if AutoPay is enabled
// - Consumer Loan (I + IsLoan): Never disabled (irrespective of autopay)
const shouldDisablePayNow = (aspireContractId: string, contracts: PaymentContract[], paymentMethod?: string) => {
    const contract = contracts?.find(c => c.AspireId === aspireContractId || c.AspireRecordId === aspireContractId);
    if (!contract) return false;

    const isConsumerLoan = contract.AccountType === 'I' && contract.IsLoan;

    // Card payments restricted to Consumer Loans only (Stripe integration)
    if (paymentMethod === 'CARD' && !isConsumerLoan) {
        return true;
    }

    // Consumer Loan (I + IsLoan): Never disable, always enabled for pay now
    if (isConsumerLoan) {
        return false;
    }

    // Consumer Lease (I + !IsLoan): Always disable
    if (contract.AccountType === 'I' && !contract.IsLoan && contract.AutoPay === true) {
        return true;
    }

    // Commercial Lease (B + !IsLoan): Always disable
    if (contract.AccountType === 'B' && !contract.IsLoan && contract.AutoPay === true) {
        return true;
    }

    // Commercial Loan (B + IsLoan): Disable if AutoPay is enabled
    if (contract.AccountType === 'B' && contract.IsLoan && contract.AutoPay === true) {
        return true;
    }

    return false;
};

/**
 * Returns true if the contract can make a payment (same logic as Pay Now on contract details).
 * Consumer loan: can always pay (when not disabled by autopay).
 * Others: can pay only when they have open invoices and are not disabled by autopay.
 * Used for Pay All button: enable when at least one contract can make payment.
 */
export function canMakePaymentForContract(
    contract: PaymentContract | null,
    openInvoices: PaymentInvoice[],
    allContracts: PaymentContract[],
    paymentMethod?: string,
): boolean {
    if (!contract) return false;
    const isConsumerLoan = contract.AccountType === 'I' && contract.IsLoan;
    if (isConsumerLoan) return !shouldDisablePayNow(String(contract.AspireId ?? ''), allContracts, paymentMethod);
    const hasOpenInvoices = (openInvoices || []).some(
        inv => String(inv?.aspireContractId ?? inv?.AspireContractId ?? '') === String(contract.AspireId),
    );
    const hasOutstandingBalance = Boolean(contract.OutstandingBalance) && Number(contract.OutstandingBalance) > 0;
    return Boolean(
        (hasOpenInvoices || hasOutstandingBalance) &&
            !shouldDisablePayNow(String(contract.AspireId ?? ''), allContracts, paymentMethod),
    );
}

interface ContractCurrentDueLike {
    CurrentDue?: string | number | null;
    NextPaymentAmount?: string | number | null;
}

export interface CurrentPaymentDueInfo {
    /** Raw amount (not currency-formatted) to display for the "current payment due" field. */
    amount: string;
    /** True when `CurrentDue` is a real, already-invoiced/open amount (not the fallback). */
    isCurrentDueActual: boolean;
}

/**
 * Single source of truth for the "current payment due" field shown on the Dashboard,
 * Contracts Overview list, and Contract Details screens. `CurrentDue` only gets populated
 * once an invoice is actually raised/open; until then it sits at 0 even though a payment is
 * clearly scheduled, so callers must fall back to `NextPaymentAmount` and reflect that via
 * `isCurrentDueActual` (used to pick the right label/styling instead of always saying
 * "Current Payment Due").
 */
export const getCurrentPaymentDueInfo = (contract?: ContractCurrentDueLike | null): CurrentPaymentDueInfo => {
    const isCurrentDueActual = Number(contract?.CurrentDue) > 0;
    return {
        amount: isCurrentDueActual ? String(contract?.CurrentDue) : String(contract?.NextPaymentAmount ?? '0'),
        isCurrentDueActual,
    };
};

/**
 * Returns true if the given date (payment DueDate) falls on the same calendar day
 * as "today" in Eastern Time (America/New_York). Payment dates from API are often
 * UTC midnight (e.g. 2026-03-05T00:00:00.000Z); we treat that as calendar date
 * 2026-03-05 and compare to current EST date, not the EST moment of that UTC time.
 */
/**
 * Extracts the calendar-day string (YYYY-MM-DD) from a due date, whether given as an ISO
 * string or a Date object. Returns null when the input can't be resolved to a calendar day.
 * Shared by isPaymentDateSameAsCurrentESTDate and isPaymentDateInPast.
 */
const toCalendarDateStr = (dueDate: string | Date): string | null => {
    if (typeof dueDate === 'string') {
        // ISO string like "2026-03-05T00:00:00.000Z" -> use date part only (calendar day)
        const paymentDateStr = dueDate.slice(0, 10);
        return paymentDateStr.length === 10 ? paymentDateStr : null;
    }
    const d = dueDate;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Returns today's calendar-day string (YYYY-MM-DD) in Eastern Time. Shared by
 * isPaymentDateSameAsCurrentESTDate and isPaymentDateInPast.
 */
const getCurrentESTDateStr = (): string => {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: ET_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const parts = formatter.formatToParts(new Date());
    const year = parts.find(p => p.type === 'year')?.value ?? '';
    const month = parts.find(p => p.type === 'month')?.value ?? '';
    const day = parts.find(p => p.type === 'day')?.value ?? '';
    return `${year}-${month}-${day}`;
};

export const isPaymentDateSameAsCurrentESTDate = (dueDate: string | Date): boolean => {
    if (dueDate == null || dueDate === '') return false;
    const paymentDateStr = toCalendarDateStr(dueDate);
    if (paymentDateStr == null) return false;
    return paymentDateStr === getCurrentESTDateStr();
};

/**
 * Returns current hour, minute, and second in Eastern Time (America/New_York).
 * Uses Intl so the result is correct regardless of the user's local timezone.
 */
export const getCurrentTimeInET = (): { hour: number; minute: number; second: number } => {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: ET_TIMEZONE,
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false,
    });
    const parts = formatter.formatToParts(now);
    const hour = parseInt(parts.find(p => p.type === 'hour')?.value ?? '0', 10);
    const minute = parseInt(parts.find(p => p.type === 'minute')?.value ?? '0', 10);
    const second = parseInt(parts.find(p => p.type === 'second')?.value ?? '0', 10);
    return { hour, minute, second };
};

/**
 * Returns current time in Eastern Time (America/New_York) as formatted string (e.g. "06:15:30 AM").
 * Use for display or tooltips in scheduled payment cancel flow.
 */
export const getCurrentESTTime = (): string => {
    return new Intl.DateTimeFormat('en-US', {
        timeZone: ET_TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    }).format(new Date());
};

/**
 * True when current time in Eastern Time is at or after 7:00:00 PM.
 * At 6:59:59 PM ET the user can still cancel / select today; at 7:00 PM ET they cannot.
 */
export const isAfter7PMET = (): boolean => {
    const { hour, minute } = getCurrentTimeInET();
    return hour > 19 || (hour === 19 && minute >= 0);
};

/**
 * True when current Eastern Time is at or after 6:00 AM.
 * Used for testing. For production, use isAfter7PMET.
 */
export const isAfter6AMEST = (): boolean => {
    const { hour, minute } = getCurrentTimeInET();
    return hour > 6 || (hour === 6 && minute >= 0);
};

/**
 * Returns true if the payment due date (calendar day in ET) is strictly before today (in ET).
 * Used to disable cancel and show "payment date is passed" message.
 */
export const isPaymentDateInPast = (dueDate: string | Date | null | undefined): boolean => {
    if (dueDate == null || dueDate === '') return false;
    const paymentDateStr = toCalendarDateStr(dueDate);
    if (paymentDateStr == null) return false;
    return paymentDateStr < getCurrentESTDateStr();
};

export interface ScheduledPaymentCancelState {
    disabled: boolean;
    tooltipMessage: string;
    toastMessage: string;
    /** True when cancel is disabled because the payment date is in the past. */
    isPastDate?: boolean;
}

const PAST_DATE_MESSAGE = 'Cancellation is not allowed as payment date is passed.';

/**
 * Central logic for scheduled payment cancel button: when to disable and what messages to show.
 * Returns disabled state and messages for tooltip and toast. All EST-based.
 * Disabled when: payment date is in the past, OR same-day and after 7 PM ET.
 */
export const getScheduledPaymentCancelState = (
    dueDate: string | Date | null | undefined,
): ScheduledPaymentCancelState => {
    const pastDate = isPaymentDateInPast(dueDate ?? '');
    if (pastDate) {
        return {
            disabled: true,
            tooltipMessage: PAST_DATE_MESSAGE,
            toastMessage: PAST_DATE_MESSAGE,
            isPastDate: true,
        };
    }
    const disabled = isPaymentDateSameAsCurrentESTDate(dueDate ?? '') && isAfter7PMET();
    const baseMessage = 'Same-day payment cancellations are no longer available after 7:00 PM Eastern Time.';
    return {
        disabled,
        tooltipMessage: disabled ? `${baseMessage}` : '',
        toastMessage: baseMessage,
        isPastDate: false,
    };
};

export const getCountryCode = (country: string): string => {
    const trimmed = country.trim();
    if (trimmed === 'United States') return 'USA';
    if (trimmed === 'Canada') return 'CAN';
    return trimmed;
};

/**
 * Returns dial code only for API (e.g. +1). UI may use '+1-ca' for Canada; this normalizes to +1.
 */
export const getDialCodeForApi = (extension: string): string =>
    extension.includes('-') ? extension.split('-')[0] : extension;

/** Consumer + Loan => editable; all other cases => non-editable. */
export const isAmountEditable = (
    contract: Record<string, unknown> | null,
    accountType: string | undefined,
): boolean => {
    if (!contract) return true;
    const isConsumer = accountType === 'I';
    return Boolean(isConsumer && contract.IsLoan);
};

/**
 * Builds a stable key for an invoice for allocation lookup.
 * Uses Id when available, otherwise contractId|dueDate|invoiceNumber.
 */
export function getOpenInvoiceAllocationKey(invoice: {
    Id?: string;
    id?: string;
    contractId?: string;
    ContractId?: string;
    DueDate?: string;
    duedate?: string;
    InvoiceNumber?: string;
    invoiceNumber?: string;
}): string {
    const id = invoice.Id ?? invoice.id;
    if (id) return String(id);
    const contractId = String(invoice.contractId ?? invoice.ContractId ?? '');
    const dueDate = String(invoice.DueDate ?? invoice.duedate ?? '');
    const invoiceNumber = String(invoice.InvoiceNumber ?? invoice.invoiceNumber ?? '');
    return `${contractId}|${dueDate}|${invoiceNumber}`;
}

export interface PendingScheduleAmountItemLike {
    contractId: string;
    totalPendingScheduleAmount: number;
}

/** Pending status for scheduled payments: 0 (enum) or string "pending" */
const SCHEDULED_PAYMENT_PENDING_STATUS = 0;

export interface PendingScheduledEntry {
    scheduledDate: string;
    amount: number;
}

/**
 * Returns non-cancelled (pending) scheduled payment entries for a contract from the
 * scheduled payments list, each with scheduled date and amount, sorted by date.
 * Used to show individual scheduled payment lines with dates in Open Invoices.
 */
export function getPendingScheduledEntriesForContract(
    contractId: string,
    scheduledPaymentsData: Array<{
        ContractId?: string;
        contractId?: string;
        AspireContractId?: string;
        aspireContractId?: string;
        PaymentDate?: string | null;
        DueDate?: string | null;
        Amount?: string | number;
        amount?: string | number;
        Status?: number | string;
        status?: number | string;
    }>,
): PendingScheduledEntry[] {
    if (!scheduledPaymentsData?.length || !contractId) return [];
    const cid = String(contractId);
    const pending = scheduledPaymentsData.filter(item => {
        const itemContractId = String(
            item.AspireContractId ?? item.aspireContractId ?? item.ContractId ?? item.contractId ?? '',
        );
        if (itemContractId !== cid) return false;
        const status = item.Status ?? item.status;
        return status === SCHEDULED_PAYMENT_PENDING_STATUS || String(status).toLowerCase() === 'pending';
    });
    return pending
        .map(item => ({
            scheduledDate: item.PaymentDate ?? item.DueDate ?? '',
            amount: Number(item.Amount ?? item.amount ?? 0) || 0,
        }))
        .filter(e => e.scheduledDate)
        .sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime());
}

/**
 * Returns only the scheduled payment entries (or portions) that apply to this invoice
 * when total scheduled is applied oldest-invoice-first. Used so the accordion shows
 * for 01/29 only the entries covering that invoice, and for 02/28 only the remainder.
 * @param pendingEntriesForContract - All pending scheduled entries for the contract (sorted by date)
 * @param allocatedToThisInvoice - Amount allocated to this invoice (paymentInProgress)
 * @param consumedByPreviousInvoices - Sum of amounts already allocated to older invoices
 */
export function getScheduledEntriesAllocatedToInvoice(
    pendingEntriesForContract: PendingScheduledEntry[],
    allocatedToThisInvoice: number,
    consumedByPreviousInvoices: number,
): PendingScheduledEntry[] {
    if (!pendingEntriesForContract?.length || allocatedToThisInvoice <= 0) return [];
    let remainingToConsume = consumedByPreviousInvoices;
    let remainingToAllocate = allocatedToThisInvoice;
    const result: PendingScheduledEntry[] = [];
    for (const entry of pendingEntriesForContract) {
        if (remainingToAllocate <= 0) break;
        if (remainingToConsume >= entry.amount) {
            remainingToConsume -= entry.amount;
            continue;
        }
        let takeFromEntry = entry.amount;
        if (remainingToConsume > 0) {
            takeFromEntry = entry.amount - remainingToConsume;
            remainingToConsume = 0;
        }
        const amountForThisInvoice = Math.min(takeFromEntry, remainingToAllocate);
        result.push({ scheduledDate: entry.scheduledDate, amount: amountForThisInvoice });
        remainingToAllocate -= amountForThisInvoice;
    }
    return result;
}

/**
 * Builds per-contract total pending scheduled amount from the scheduled payments list
 * (the same list shown on the Scheduled Payment tab). Use this so Open Invoices
 * reflects newly added scheduled payments (e.g. $3.45) that may not yet be in the
 * open-invoices API's pendingScheduleAmount.
 */
export function getPendingScheduleAmountFromScheduledPayments(
    scheduledPaymentsData: Array<{
        ContractId?: string;
        contractId?: string;
        AspireContractId?: string;
        aspireContractId?: string;
        Amount?: string | number;
        amount?: string | number;
        Status?: number | string;
        status?: number | string;
    }>,
): PendingScheduleAmountItemLike[] {
    if (!scheduledPaymentsData?.length) return [];
    const byContract = new Map<string, number>();
    for (const item of scheduledPaymentsData) {
        const status = item.Status ?? item.status;
        const isPending = status === SCHEDULED_PAYMENT_PENDING_STATUS || String(status).toLowerCase() === 'pending';
        if (!isPending) continue;
        const contractId = String(
            item.AspireContractId ?? item.aspireContractId ?? item.ContractId ?? item.contractId ?? '',
        );
        if (!contractId) continue;
        const amount = Number(item.Amount ?? item.amount ?? 0) || 0;
        byContract.set(contractId, (byContract.get(contractId) ?? 0) + amount);
    }
    return Array.from(byContract.entries()).map(([contractId, totalPendingScheduleAmount]) => ({
        contractId,
        totalPendingScheduleAmount,
    }));
}

/**
 * Allocates each contract's total scheduled payment across its open invoices in
 * oldest-due-date-first order. Used so that one scheduled payment is applied to
 * the oldest invoice first, then the next, etc. (less payment covers oldest;
 * over payment can cover multiple invoices).
 * Returns a Map from invoice allocation key to the amount allocated to that invoice.
 */
export function getScheduledAmountAllocatedPerInvoice(
    invoices: Array<{
        Id?: string;
        id?: string;
        contractId?: string;
        ContractId?: string;
        DueDate?: string;
        duedate?: string;
        Amount?: string | number;
        amount?: string | number;
        InvoiceNumber?: string;
        invoiceNumber?: string;
    }>,
    pendingScheduleAmount: PendingScheduleAmountItemLike[],
): Map<string, number> {
    const map = new Map<string, number>();
    if (!invoices?.length || !pendingScheduleAmount?.length) return map;

    const byContract = new Map<string, typeof invoices>();
    for (const inv of invoices) {
        const cid = String(inv.contractId ?? inv.ContractId ?? '');
        if (!cid) continue;
        if (!byContract.has(cid)) byContract.set(cid, []);
        byContract.get(cid)!.push(inv);
    }

    for (const [contractId, contractInvoices] of byContract) {
        const totalScheduled =
            Number(
                pendingScheduleAmount.find(p => String(p.contractId) === contractId)?.totalPendingScheduleAmount ?? 0,
            ) || 0;
        const sorted = [...contractInvoices].sort((a, b) => {
            const dA = a.DueDate ?? a.duedate;
            const dB = b.DueDate ?? b.duedate;
            const dateA = dA != null ? new Date(dA).getTime() : 0;
            const dateB = dB != null ? new Date(dB).getTime() : 0;
            return dateA - dateB;
        });
        let remaining = totalScheduled;
        for (const inv of sorted) {
            const amountDue = Number(inv.amount ?? inv.Amount ?? 0) || 0;
            const allocated = Math.max(0, Math.min(amountDue, remaining));
            remaining = Math.max(0, remaining - allocated);
            const key = getOpenInvoiceAllocationKey(inv);
            map.set(key, allocated);
        }
    }
    return map;
}

/** Integer digit count of remaining principal (floor of balance). */
function getRemainingPrincipalIntegerDigitCount(balance: number): number {
    if (!Number.isFinite(balance) || balance === 0) return 0;
    return String(Math.floor(Math.abs(balance))).length;
}

/**
 * Consumer loan amount field: at most 2 decimal places; optional cap on integer digits (e.g. 9 when principal is 10–11 digits).
 */
function normalizeConsumerLoanAmountInput(raw: string, maxIntegerDigits?: number): string {
    const s = String(raw).replace(/[^\d.]/g, '');
    const dotIdx = s.indexOf('.');
    let intPart = dotIdx === -1 ? s : s.slice(0, dotIdx);
    let frac = dotIdx === -1 ? '' : s.slice(dotIdx + 1).replace(/\./g, '');
    frac = frac.slice(0, 2);
    if (maxIntegerDigits != null && intPart.length > maxIntegerDigits) {
        intPart = intPart.slice(0, maxIntegerDigits);
    }
    if (dotIdx !== -1) {
        return frac.length > 0 ? `${intPart}.${frac}` : `${intPart}.`;
    }
    return intPart;
}

export {
    ALLOWED_FILE_EXTENSIONS,
    detectInputType,
    downloadBlobSafely,
    formatCurrency,
    formatDateWithTimeWithSeconds,
    formatTime,
    formatToUSDate,
    formatToUSDateWithTime,
    getGreeting,
    getMaskedEmailForProp,
    getMaskedPhoneNumberForProp,
    getMimeType,
    isDocumentPreviewEligible,
    isValidContractId,
    isValidPostalCode,
    isValidSSNNumber,
    regex,
    restrictedExtensions,
    shouldDisablePayNow,
    getRemainingPrincipalIntegerDigitCount,
    normalizeConsumerLoanAmountInput,
};
